import { debounce } from '@theme/utilities';

class FaqMain extends HTMLElement {

    /** @type {HTMLScriptElement} */
    get data() {
        const data = this.querySelector('.faq-main__data');

        if (!(data instanceof HTMLScriptElement)) throw new Error('Data element not found');

        return data;
    }

    /** @type {HTMLElement} */
    get searchContainer() {
        const searchContainer = this.querySelector('.faq-main-search');

        if (!(searchContainer instanceof HTMLElement)) throw new Error('Search container element not found');

        return searchContainer;
    }

    /** @type {HTMLInputElement} */
    get searchInput() {
        const searchInput = this.querySelector('.faq-main-search__input');

        if (!(searchInput instanceof HTMLInputElement)) throw new Error('Search input element not found');

        return searchInput;
    }

    /** @type {HTMLButtonElement} */
    get searchClear() {
        const searchClear = this.querySelector('.faq-main-search__clear');

        if (!(searchClear instanceof HTMLButtonElement)) throw new Error('Search clear element not found');

        return searchClear;
    }

    /** @type {HTMLElement} */
    get searchResultsContainer() {
        const searchResultsContainer = this.querySelector('.faq-main-search__results');

        if (!(searchResultsContainer instanceof HTMLElement)) throw new Error('Search results container element not found');

        return searchResultsContainer;
    }

    get noResultsText() {
        return this.dataset.noResultsText;
    }

    connectedCallback() {

        this.initObserver();

        this.handleClearEvents()

        this.handleCloseEvents();

        this.faqData = this.parseData();
        if (this.faqData === null) {
            console.error('FAQ - unable to access FAQ data');
            return;
        }

        this.handleSearch();
    }

    /**
     * Listens for new search result items,
     * and adds a click event listener
     */
    initObserver() {
        const observer = new MutationObserver(() => {
            const resultItems = this.searchResultsContainer.querySelectorAll('.faq-main-search__results__item:not(.faq-main-search__results__item--no-results)');
            for (const resultItem of resultItems) {
                resultItem.addEventListener('click', () => { this.handleResultClick(resultItem); });
            }
        });
        observer.observe(this.searchResultsContainer, {
            subtree: true,
            childList: true
        });
    }

    /**
     * Handles click events for search result items
     *
     * @param {Element} resultItem - The result item.
     */
    handleResultClick(resultItem) {

        // Remove classes from search
        this.searchContainer.classList.remove('faq-main-search--results-active');

        // Handleize the selected item title so we can find the correct FAQ item
        let selectedItemContent = resultItem.innerHTML.toLowerCase();
        selectedItemContent = selectedItemContent.replace('&amp;', '');
        selectedItemContent = selectedItemContent.replace('&', '');
        selectedItemContent = selectedItemContent.replace(/[^\w\s]/gi, '');
        selectedItemContent = selectedItemContent.replace(/[^a-z0-9]+/g, '-');
        selectedItemContent = selectedItemContent.replace(/-$/, '');
        selectedItemContent = selectedItemContent.replace(/^-/, '');

        // Open the correct FAQ accordion item
        const titleIdentifier = selectedItemContent;
        const matchingFaqItem = this.querySelector(`[data-faq-identifier=${titleIdentifier}]`);
        if (matchingFaqItem === null) {
            return;
        }
        matchingFaqItem.querySelector('details')?.setAttribute('open', '');

        // Scroll page to the correct FAQ accordion item
        const elementRect = matchingFaqItem.getBoundingClientRect();
        let scrollPosition = elementRect.top;
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        scrollPosition = absoluteElementTop - (window.innerHeight / 2);
        window.scroll({
            top: scrollPosition,
            left: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Parses FAQ data to a JS object
     */
    parseData() {
        let data = null;
        if (this.data) {
            try {
                data = JSON.parse(this.data.innerHTML);
            } catch(event) {
                console.error('FAQ parse error', {
                    event
                });
            }
        }
        return data;
    }

    /**
     * Listens for search input and updates results markup
     */
    handleSearch() {
        this.searchInput.addEventListener('input', debounce(() => {
            this.searchContainer.classList.add('faq-main-search--results-active');
            const enteredText = this.searchInput.value;
            const results = this.filterResults(enteredText);
            this.updateResultsMarkup(results);
            if (enteredText == '') {
                this.searchContainer.classList.remove('faq-main-search--input-has-value');
            } else {
                this.searchContainer.classList.add('faq-main-search--input-has-value');
            }
        }, 100));
    }

    /**
     * Using the entered search terms, check FAQ
     * data to find appropriate QA's, max 5
     * 
     * @param {string} enteredText - The entered search terms.
     */
    filterResults(enteredText) {
        let results = [];
        if (this.faqData !== null) {
            for (const data of this.faqData) {
                const valueToCheck = enteredText.toLowerCase();
                const questionToCheck = data.question.toLowerCase();
                const answerToCheck = data.answer.toLowerCase();
                if (questionToCheck.includes(valueToCheck) || answerToCheck.includes(valueToCheck)) {
                    results.push(data.question);
                }
            }
        }
        results = results.slice(0, 5);
        return results;
    }

    /**
     * Given result strings, build markup to show the user
     * 
     * @param {string[]} results - An array of result strings.
     */
    updateResultsMarkup(results) {
        let resultsMarkup = '';
        for (const result of results) {
            const resultMarkup = `
                <div class="faq-main-search__results__item">
                    ${result}
                </div>
            `;
            resultsMarkup += resultMarkup;
        }
        if (resultsMarkup === '') {
            resultsMarkup = `
                <div class="faq-main-search__results__item faq-main-search__results__item--no-results">
                    ${this.noResultsText || 'No results found'}
                </div>
            `;
        }
        this.searchResultsContainer.innerHTML = resultsMarkup;
    }

    /**
     * Clear search input value when clear button is clicked
     */
    handleClearEvents() {
        this.searchClear.addEventListener('click', () => {
            this.searchInput.value = '';
            this.searchInput.focus();
            this.searchContainer.classList.remove('faq-main-search--input-has-value');
        });
    }

    /**
     * Close search results when clicking outside of the search container
     */
    handleCloseEvents() {
        document.body.addEventListener('click', (e) => {
            const clickedElement = e.target;
            if (clickedElement == this.searchContainer) {
                return;
            }
            this.searchContainer.classList.remove('faq-main-search--results-active');
        });
    }
}

if (!customElements.get('faq-main')) {
    customElements.define('faq-main', FaqMain);
}
